import { api } from "@/api"
import type { TableOrdering } from "@/components/BaseTable.vue"
import type {
  PaginatedResponse,
  PaginationQuery,
} from "@/utils/utilityTypes"

import { useQuery } from "@tanstack/vue-query"
import type { MaybeRef } from "@vueuse/core"

export type MerchantGroupTerminal = {
  id: number
  terminalId: string
  clientName: string
  schemaName: string
  registrationDate: Date | null
  lastPaired: Date | null
  lastPairedVersion: string | null
  isPaired: boolean
}

export type ListMerchantGroupTerminalsFilter = {
  search?: string
  schemaName?: string
}

export type ListMerchantGroupTerminalsOrdering =
  | "terminalId"
  | "name"
  | "lastPaired"
  | "lastPairedVersion"
  | "registrationDate"

export type ListMerchantGroupTerminalsQuery =
  PaginationQuery &
    ListMerchantGroupTerminalsFilter & {
      ordering?: TableOrdering<ListMerchantGroupTerminalsOrdering>
    }

const listMerchantGroupTerminals = async (
  query: ListMerchantGroupTerminalsQuery
): Promise<PaginatedResponse<MerchantGroupTerminal>> => {
  const { body } = await api.get<
    PaginatedResponse<
      RecursivelyConvertDatesToStrings<MerchantGroupTerminal>
    >
  >("/merchant-group/self/terminals/", {
    query: query,
    isMerchantGroup: true,
  })

  return {
    ...body,
    items: body.items.map((terminal) => ({
      ...terminal,
      lastPaired: terminal.lastPaired
        ? DateFns.parseISO(terminal.lastPaired)
        : null,
      registrationDate: terminal.registrationDate
        ? DateFns.parseISO(terminal.registrationDate)
        : null,
    })),
  }
}

export const useMerchantGroupTerminals = (
  query: MaybeRef<ListMerchantGroupTerminalsQuery>
) =>
  useQuery({
    queryKey: [
      BASE_MERCHANT_GROUP_TERMINALS_QUERY_KEY,
      "index",
      query,
    ] as const,
    queryFn: ({
      queryKey: [_basekey, _routekey, query_],
    }) => listMerchantGroupTerminals(unref(query_)),
  })
